// Panel chat "TAKA": anak bisa bertanya tentang soal yang baru dijawab salah (mode "soal"),
// atau tentang materi yang sedang dipelajari di menu belajar (mode "materi").
// Konteks dikirim ke /api/taka; semua pengaman ada di server (lib/taka.js).
import { $, layarSentuh } from '../core/dom.js';
import { esc, F } from '../core/format.js';
import { mascot } from './mascot.js';
import { S, panggil } from './state.js';
import { keyHTML, userHTML } from './jawaban.js';
import { MODE_DEMO } from './riwayat.js';
import { sfx } from './sound.js';
import { dukungTTS, dukungSTT, suaraTakaNyala, aturSuaraTaka, bicara, berhentiBicara, dengarkan } from './suara.js';

const IKON_SPEAKER = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor"/><path d="M16 8.5a5 5 0 0 1 0 7M18.5 6a8.5 8.5 0 0 1 0 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
const IKON_MIC = '<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true"><rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor"/><path d="M6 11a6 6 0 0 0 12 0M12 17v4M9 21h6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
let berhentiDengar = null;

const MAKS_PERTANYAAN = 10;
const SARAN = ['Kenapa jawabanku salah?', 'Jelaskan langkah ini lagi', 'Kasih contoh soal yang mirip'];
const percakapan = new WeakMap(); // soal/modul -> { pesan: [], sisa }
let panelAktif = null;

// Teks polos dari HTML soal (gambar SVG dibuang, tabel diambil terpisah).
function teks(html) {
  const d = document.createElement('div');
  d.innerHTML = html || '';
  d.querySelectorAll('svg, table').forEach(x => x.remove());
  return d.textContent.replace(/\s+/g, ' ').trim();
}
function tabel(html) {
  const d = document.createElement('div');
  d.innerHTML = html || '';
  return [...d.querySelectorAll('table')].map(tb => [tb.caption?.textContent || '', ...[...tb.rows].map(r => [...r.cells].map(c => c.textContent.trim()).join(' | '))].filter(Boolean).join('; ')).join(' || ');
}

export function konteksSoal(q, sel, langkahAktif) {
  return {
    judul: teks(q.title), elemen: q.el, tipe: q.type, soal: teks(q.text), tabel: tabel(q.vis),
    pilihan: q.type === 'bs' ? q.statements.map(s => teks(s.t)) : q.type === 'pg' ? q.options.map(teks) : q.options.map(o => teks(o.t)),
    kunci: teks(keyHTML(q).replace(/<\/li>/g, '; </li>')), jawabanAnak: teks(userHTML(q, sel)),
    langkah: q.steps.map(s => [s.t && teks(s.t) + ' ' + tabel(s.v || ''), s.ask && `Hitungan: ${teks(s.ask.q)} jawabannya ${s.ask.a}${s.ask.u ? ' ' + teks(s.ask.u) : ''}`, s.after && teks(s.after)].filter(Boolean).join(' | ')),
    langkahAktif,
    mapel: S.mapel,
    ...(q.bacaan ? { bacaan: `${q.bacaan.judul}. ${q.bacaan.teks.replace(/^#\s+/gm, '')}`, bukti: q.bukti || [] } : {}),
    alasan: q.type === 'pg' ? (q.why || []).map(teks) : (q.type === 'bs' ? q.statements : q.options).map(x => teks(x.w || '')),
  };
}

// Tampilkan balasan: escape dulu, lalu **tebal**, baris baru, dan pecahan angka a/b.
const tampil = t => esc(t).replace(/\*\*(.+?)\*\*/g, '<b>$1</b>').replace(/\b(\d{1,4})\/(\d{1,4})\b/g, (_, a, b) => F(a, b)).replace(/\n/g, '<br>');

/**
 * Buka panel TAKA untuk soal yang baru dijawab salah.
 * @param {{q: object, sel: any, langkahAktif: () => number|null}} opsi
 */
export function bukaTaka({ q, sel, langkahAktif }) {
  bukaPanelTaka($('#taka'), {
    kunci: q, mode: 'soal', konteks: () => konteksSoal(q, sel, langkahAktif?.() ?? null),
    sapa: n => `Hai ${n}! Aku TAKA. Ada bagian soal ini yang membuatmu bingung? Tanyakan saja, ya!`,
    saran: () => SARAN, placeholder: 'Tulis pertanyaanmu tentang soal ini...',
    sisa: n => `Sisa ${n} pertanyaan untuk soal ini.`, habis: 'Pertanyaan untuk soal ini sudah habis. Coba ikuti langkah pembahasannya, ya!',
  });
}

/**
 * Panel TAKA umum.
 * @param {HTMLElement} panel
 * @param {{kunci: object, mode: 'soal'|'materi', konteks: () => object, sapa: (nama) => string, saran: () => string[],
 *          placeholder: string, sisa: (n) => string, habis: string, maks?: number, onTutup?: () => void}} o
 */
export function bukaPanelTaka(panel, o) {
  if (panelAktif && panelAktif !== panel) tutupTaka();
  panelAktif = panel;
  panel._onTutup = o.onTutup;
  if (!percakapan.has(o.kunci)) percakapan.set(o.kunci, { pesan: [], sisa: o.maks || MAKS_PERTANYAAN });
  const st = percakapan.get(o.kunci), nama = panggil();
  panel.innerHTML = `<div class="taka-head"><span class="taka-avatar">${mascot('happy', 52)}</span><div class="grow"><b>TAKA</b><span>Teman belajarmu</span></div>${dukungTTS ? `<button class="btn btn-sm taka-suara" data-suara type="button" aria-pressed="${suaraTakaNyala()}">${IKON_SPEAKER}<span></span></button>` : ''}<button class="btn btn-sm" data-tutup type="button">Tutup</button></div>
    <div class="taka-msgs scroll" aria-live="polite"></div>
    <div class="taka-saran"></div>
    <form class="taka-form" autocomplete="off">${dukungSTT && !MODE_DEMO ? `<button class="taka-mic" type="button" aria-label="Tahan untuk bicara">${IKON_MIC}</button>` : ''}<input class="input" maxlength="300" placeholder="${dukungSTT ? 'Ketik, atau tahan tombol mikrofon untuk bicara...' : esc(o.placeholder)}" aria-label="Pertanyaan untuk TAKA"><button class="btn btn-sm btn-go" data-kirim type="submit">Kirim</button></form>
    <p class="taka-note"></p>`;
  panel.hidden = false;
  const msgs = panel.querySelector('.taka-msgs'), form = panel.querySelector('form'), inp = form.querySelector('input'), saran = panel.querySelector('.taka-saran'), note = panel.querySelector('.taka-note');
  const avatar = panel.querySelector('.taka-avatar'), mic = form.querySelector('.taka-mic'), tSuara = panel.querySelector('[data-suara]');
  let catatanSementara = '';

  // ---------- suara TAKA ----------
  const ucapkan = teks => bicara(teks, { onMulai: () => avatar.classList.add('bicara'), onSelesai: () => avatar.classList.remove('bicara') });
  const labelSuara = () => { if (!tSuara) return; const on = suaraTakaNyala(); tSuara.setAttribute('aria-pressed', on); tSuara.querySelector('span').textContent = on ? 'Suara nyala' : 'Suara mati'; };
  tSuara?.addEventListener('click', () => { aturSuaraTaka(!suaraTakaNyala()); labelSuara(); if (!suaraTakaNyala()) { berhentiBicara(); avatar.classList.remove('bicara'); } });
  labelSuara();

  const gelembung = (peran, html, cls = '', teksSuara = '') => {
    const dengar = peran === 'taka' && dukungTTS && teksSuara ? `<button class="taka-dengar" type="button" aria-label="Dengarkan lagi">${IKON_SPEAKER}</button>` : '';
    msgs.insertAdjacentHTML('beforeend', `<div class="taka-msg dari-${peran} ${cls}">${peran === 'taka' ? mascot('happy', 30) : ''}<div class="taka-bubble">${html}</div>${dengar}</div>`);
    const b = msgs.lastElementChild.querySelector('.taka-dengar');
    if (b) b.onclick = () => ucapkan(teksSuara);
    msgs.scrollTop = msgs.scrollHeight;
  };
  const perbarui = () => {
    const habis = st.sisa <= 0 || MODE_DEMO;
    inp.disabled = form.querySelector('[data-kirim]').disabled = habis;
    if (mic) mic.disabled = habis;
    saran.innerHTML = habis ? '' : o.saran().map(s => `<button class="chip-btn" type="button">${esc(s)}</button>`).join('');
    saran.querySelectorAll('button').forEach(b => b.onclick = () => kirim(b.textContent));
    note.textContent = catatanSementara || (MODE_DEMO ? 'TAKA hanya bisa dipakai di aplikasi versi online.' : st.sisa > 0 ? `${o.sisa(st.sisa)}${dukungSTT ? ' Tahan tombol mikrofon untuk bicara.' : ''}` : o.habis);
  };

  const sapa = o.sapa(nama);
  gelembung('taka', sapa, '', sapa);
  st.pesan.forEach(m => gelembung(m.peran, m.peran === 'taka' ? tampil(m.teks) : esc(m.teks), '', m.teks));
  if (MODE_DEMO) gelembung('taka', 'Di versi pratinjau ini TAKA belum bisa menjawab. Minta guru atau orang tuamu membuka aplikasi versi online, ya!', 'info');
  perbarui();
  if (suaraTakaNyala() && !st.pesan.length) ucapkan(sapa);

  let sibuk = false;
  async function kirim(t) {
    t = String(t || '').trim();
    if (!t || sibuk || st.sisa <= 0 || MODE_DEMO) return;
    sibuk = true; inp.value = ''; sfx.tap(); berhentiBicara();
    gelembung('anak', esc(t));
    msgs.insertAdjacentHTML('beforeend', `<div class="taka-msg dari-taka mengetik">${mascot('think', 30)}<div class="taka-bubble"><span class="dots"><i></i><i></i><i></i></span></div></div>`);
    msgs.scrollTop = msgs.scrollHeight;
    let jawaban = '';
    try {
      const res = await fetch('/api/taka', { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ mode: o.mode, konteks: o.konteks(), riwayat: st.pesan, pesan: t }) });
      const d = await res.json().catch(() => ({}));
      msgs.querySelector('.mengetik')?.remove();
      if (!res.ok || !d.ok) {
        jawaban = res.status === 401 ? 'Sesi masukmu sudah habis. Keluar lalu masuk lagi, ya.' : d.pesan || 'Maaf, TAKA sedang kesulitan menjawab.';
        gelembung('taka', esc(jawaban), 'info', jawaban);
      } else {
        // Penolakan tidak disimpan sebagai riwayat supaya tidak ikut dikirim ke model.
        if (!d.ditolak) st.pesan.push({ peran: 'anak', teks: t }, { peran: 'taka', teks: d.balasan, tanda: d.tanda });
        st.sisa--;
        jawaban = d.balasan;
        gelembung('taka', tampil(d.balasan), d.ditolak ? 'info' : '', d.balasan);
      }
    } catch (e) {
      msgs.querySelector('.mengetik')?.remove();
      jawaban = 'TAKA perlu internet untuk menjawab. Periksa koneksimu, lalu coba lagi.';
      gelembung('taka', jawaban, 'info', jawaban);
    }
    sibuk = false;
    perbarui();
    if (suaraTakaNyala() && jawaban && !panel.hidden) ucapkan(jawaban);
    if (!layarSentuh()) inp.focus({ preventScroll: true });
  }

  // ---------- tekan-tahan untuk bicara ----------
  if (mic) {
    let aktif = false, sebelum = '';
    const mulai = e => {
      e.preventDefault();
      if (aktif || mic.disabled) return;
      aktif = true; sebelum = inp.value.trim(); berhentiBicara(); avatar.classList.remove('bicara');
      mic.classList.add('dengar'); mic.setAttribute('aria-pressed', 'true');
      catatanSementara = 'TAKA mendengarkan... lepaskan tombol kalau sudah selesai.'; perbarui();
      try { mic.setPointerCapture?.(e.pointerId); } catch (x) { /* bukan pointer */ }
      berhentiDengar = dengarkan({
        onTeks: t => { inp.value = (sebelum ? sebelum + ' ' : '') + t; },
        onSelesai: t => {
          aktif = false; berhentiDengar = null; mic.classList.remove('dengar'); mic.setAttribute('aria-pressed', 'false');
          inp.value = ((sebelum ? sebelum + ' ' : '') + t).trim().slice(0, 300);
          catatanSementara = inp.value ? 'Periksa tulisannya dulu. Kalau sudah benar, tekan Kirim.' : '';
          perbarui();
          if (inp.value) form.querySelector('[data-kirim]').focus({ preventScroll: true });
        },
        onGalat: pesan => { catatanSementara = pesan; perbarui(); },
      });
    };
    const lepas = () => { if (aktif) berhentiDengar?.(); };  // berhentiDengar aman dipanggil berkali-kali
    mic.addEventListener('pointerdown', mulai);
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(ev => mic.addEventListener(ev, lepas));
    mic.addEventListener('contextmenu', e => e.preventDefault());
    mic.addEventListener('keydown', e => { if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) mulai(e); });
    mic.addEventListener('keyup', e => { if (e.key === ' ' || e.key === 'Enter') lepas(); });
    inp.addEventListener('input', () => { if (catatanSementara) { catatanSementara = ''; perbarui(); } });
  }

  form.addEventListener('submit', e => { e.preventDefault(); catatanSementara = ''; kirim(inp.value); });
  panel.querySelector('[data-tutup]').onclick = tutupTaka;
  if (!layarSentuh()) setTimeout(() => inp.focus({ preventScroll: true }), 50);
}

export function tutupTaka() {
  berhentiDengar?.(); berhentiDengar = null;
  berhentiBicara();
  const p = panelAktif || $('#taka');
  panelAktif = null;
  if (p) { p.hidden = true; p.innerHTML = ''; p._onTutup?.(); p._onTutup = null; }
}
export const takaTerbuka = () => !!panelAktif && !panelAktif.hidden;
